import React, { useState } from 'react'
import { ModalWrapper } from 'Components'
import { handleError, handleSuccess } from 'Helpers'
import fillingStationsService, {
  FillingStation,
} from 'BoostXpressAdmin/Services/fillingStationsService'
import { getTankNamesError, tanksToSave } from './tankNames'
import './EditTankModal.scss'

interface EditTankModalProps {
  station: FillingStation
  tankIndex: number
  onClose: () => void
  onSuccess: () => void
}

const EditTankModal: React.FC<EditTankModalProps> = ({
  station,
  tankIndex,
  onClose,
  onSuccess,
}) => {
  const tank = station.tanks[tankIndex]
  const [name, setName] = useState(tank.name ?? '')
  const [product, setProduct] = useState(tank.product)
  const [capacity, setCapacity] = useState(String(tank.quantity))
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [validationError, setValidationError] = useState('')
  const products = Array.from(
    new Set([tank.product, ...(station.products ?? []).map((item) => item.product)]),
  ).filter(Boolean)

  const handleClose = () => {
    if (!isSubmitting) onClose()
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (isSubmitting) return
    const quantity = Number(capacity)
    if (!product || !capacity.trim() || !Number.isFinite(quantity) || quantity <= 0) {
      setValidationError('Select a product and enter a capacity greater than zero.')
      return
    }

    setValidationError('')
    setIsSubmitting(true)
    try {
      // Read the latest array so saving one tank preserves other tanks' changes.
      const latest = await fillingStationsService.getById(station.id)
      if (!latest.status || !latest.data)
        throw new Error('Could not load the latest station tanks.')
      const latestTanks = latest.data.tanks ?? []
      const currentTank = latestTanks[tankIndex]
      if (
        !currentTank ||
        currentTank.product !== tank.product ||
        currentTank.quantity !== tank.quantity ||
        currentTank.name !== tank.name
      ) {
        setValidationError(
          'This tank has changed. Close the editor and refresh the station before editing again.',
        )
        return
      }
      const tanks = tanksToSave(
        latestTanks.map((item, index) =>
          index === tankIndex ? { product, quantity, name } : item,
        ),
      )
      const namesError = getTankNamesError(tanks)
      if (namesError) {
        setValidationError(`${namesError} To change naming for multiple tanks, use Edit Station.`)
        return
      }
      const response = await fillingStationsService.updateTanks(station.id, tanks)
      if (!response.status) throw new Error(response.message || 'Could not update the tank.')
      handleSuccess('Tank updated successfully')
      onClose()
      onSuccess()
    } catch (error) {
      handleError(error)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <ModalWrapper showModal closeModal={handleClose} title='Edit Tank' hideHeader={false}>
      <form className='edit-tank-modal' onSubmit={handleSubmit} aria-busy={isSubmitting}>
        <div className='edit-tank-modal__field'>
          <label htmlFor='edit-tank-name'>Tank name (optional)</label>
          <input
            id='edit-tank-name'
            className='form-control'
            value={name}
            onChange={(event) => setName(event.target.value)}
            disabled={isSubmitting}
            aria-describedby='edit-tank-name-hint'
          />
          <p id='edit-tank-name-hint' className='edit-tank-modal__hint'>
            All tanks at a station must be named, or all left unnamed.
          </p>
        </div>
        <div className='edit-tank-modal__field'>
          <label htmlFor='edit-tank-product'>Product</label>
          <select
            id='edit-tank-product'
            className='form-control'
            value={product}
            onChange={(event) => setProduct(event.target.value)}
            required
            disabled={isSubmitting}
          >
            {products.map((item) => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </select>
        </div>
        <div className='edit-tank-modal__field'>
          <label htmlFor='edit-tank-capacity'>Capacity ({product === 'LPG' ? 'kg' : 'L'})</label>
          <input
            id='edit-tank-capacity'
            className='form-control'
            type='number'
            min='0'
            step='any'
            value={capacity}
            onChange={(event) => setCapacity(event.target.value)}
            required
            disabled={isSubmitting}
          />
        </div>
        {validationError && (
          <p className='edit-tank-modal__error' role='alert'>
            {validationError}
          </p>
        )}
        <div className='edit-tank-modal__actions'>
          <button
            type='button'
            className='edit-tank-modal__cancel'
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button type='submit' className='edit-tank-modal__submit' disabled={isSubmitting}>
            {isSubmitting ? 'Updating...' : 'Update Tank'}
          </button>
        </div>
      </form>
    </ModalWrapper>
  )
}

export default EditTankModal
